// @ts-nocheck
import { Box, DrawerCloseButton, DrawerContent, Flex, useDisclosure, useMediaQuery } from '@chakra-ui/react';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import { useContext, useEffect, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import Banner from 'components/Banner';
import { ControllableDrawer } from 'components/Drawer';
import { ErrorBoundaryInner } from 'components/ErrorBoundary';
import Header from 'components/Header';
// import Loader from 'components/Loader';
import LogoLoader from 'components/Loader/LogoLoader';
import PageLoader from 'components/Loader/PageLoader';
import Profitwell from 'components/Profitwell';
import Sidebar from 'components/Sidebar';
import { TrackerContext } from 'components/TrackerProvider/Provider';
import { LARGE_NAV_SIZE, SMALL_NAV_SIZE } from 'global/appConstants';
import { roles } from 'global/rolesAndPermissions';
import { useLogout } from 'hooks/auth/useLogout';
import { useHubspot } from 'hooks/common/useHubspot';
// import { useUserFlow } from 'hooks/common/useUserflow';
import { useUserGuiding } from 'hooks/common/useUserGuiding';
import { useFetchUserDetails } from 'hooks/profile/useFetchUserDetail';
import { useGlobalLoader } from 'store/useGlobalLoader';
import { useUserStore } from 'store/useUserStore';
import toaster from 'utils/toaster';
import token from 'utils/token';

const Home = () => {
  const tracker = useContext(TrackerContext);
  const [isLargerThan768, isLargerThan992] = useMediaQuery(['(min-width: 768px)', '(min-width: 992px)'], {
    ssr: false,
  });
  const { isOpen: isNavOpen, onOpen, onClose, onToggle: onNavToggle } = useDisclosure();

  const { user, headerHeight } = useUserStore(state => ({
    user: state.user as any,
    headerHeight: state.headerHeight,
  }));
  const { start, stop } = useGlobalLoader();
  const { isLoading } = useFetchUserDetails();

  const handleLogout = useLogout();

  useHubspot();
  useUserGuiding();
  // useUserFlow();

  // Initialize tracker
  useEffect(() => {
    if (user) {
      tracker?.initUser(user.userInfo?.email);
      if (user.userInfo?.ipsu) {
        tracker?.setMetadata('ipsu', user.userInfo.ipsu);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracker]);

  // Global lottie loader
  useEffect(() => {
    if (isLoading) {
      start();
    } else {
      stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  /**
   * Token expiry date Timeout watcher
   * Immediately logout users when timemout ends
   *  */
  useEffect(() => {
    const expiryDateInNumber: string | null = token.getTokenExpiryDate();
    let tokenTimeout: any;

    if (expiryDateInNumber) {
      const expiryDate = new Date(+expiryDateInNumber * 1000);
      const expiryDurationInMilliseconds = differenceInMilliseconds(expiryDate, new Date());

      tokenTimeout = setTimeout(() => {
        handleLogout();
        toaster.error({ description: 'You have been logged out.' });
      }, expiryDurationInMilliseconds);
    }

    // Open sidebar if screen is larger than 992px
    if (isLargerThan992) {
      onOpen();
    }

    return () => {
      if (tokenTimeout) {
        clearTimeout(tokenTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Profitwell
        authToken={import.meta.env.VITE_PROFITWELL_PUBLIC_API}
        shouldNotLoad={import.meta.env.DEV}
        email={user?.userInfo?.email}
      />
      {isLargerThan768 ? (
        <Flex
          as="aside"
          h="100%"
          position="fixed"
          top="0"
          left="0"
          width={isNavOpen ? LARGE_NAV_SIZE : SMALL_NAV_SIZE}
          flexDir="column"
          bg="primary.900"
          zIndex="docked"
          className="hidden-print"
        >
          <Sidebar isNavOpen={isNavOpen} />
        </Flex>
      ) : (
        <ControllableDrawer isOpen={isNavOpen} onClose={onClose} placement="left" isFullHeight>
          <DrawerContent bg="primary.900" height="100%" width={`${LARGE_NAV_SIZE} !important`} className="hidden-print">
            <DrawerCloseButton color="white" />
            <Sidebar isNavOpen={isNavOpen} onNavClose={onClose} />
          </DrawerContent>
        </ControllableDrawer>
      )}
      <Box
        width="100%"
        paddingLeft={isLargerThan768 ? (isNavOpen ? LARGE_NAV_SIZE : SMALL_NAV_SIZE) : 0}
        minHeight="100vh"
      >
        <Header isNavOpen={isNavOpen} onNavToggle={onNavToggle} />
        <Box as="main" width="100%" overflowX="hidden" minHeight={`calc(100vh - ${headerHeight}px)`}>
          <Suspense fallback={<LogoLoader />}>
            <ErrorBoundaryInner>
              <Outlet />
            </ErrorBoundaryInner>
          </Suspense>
        </Box>
      </Box>
    </>
  );
};

export default Home;
